import {
  CreateProjectRequest,
  CreateProjectResponse,
  GetProjectRequest,
  GetProjectResponse,
  UpdateProjectRequest,
  UpdateProjectResponse,
  UpdateProjectStakeholdersRequest,
  UpdateProjectStakeholdersResponse,
  DeleteProjectStakeholdersRequest,
  DeleteProjectStakeholdersResponse,
  GetProjectTicketsRequest,
  GetProjectTicketsResponse,
  UploadRequirementsSheetRequest,
  UploadRequirementsSheetResponse,
  DownloadRequirementsSheetRequest,
} from ".";
import {
  restResourceDownloader,
  restResourceRequester,
  restResourceUploader,
} from "../rest-resource-requester";
import { UpdateProjectMemoRequest } from "./update-project-memo/UpdateProjectMemoRequest";
import { UpdateProjectMemoResponse } from "./update-project-memo/UpdateProjectMemoResponse";

interface ProjectsRestResource {
  /**
   * GET /
   */
  getProject(request: GetProjectRequest): Promise<GetProjectResponse>;
  getProjectTickets(
    request: GetProjectTicketsRequest,
  ): Promise<GetProjectTicketsResponse>;
  downloadRequirementsSheet(
    request: DownloadRequirementsSheetRequest,
  ): Promise<void>;

  /**
   * PUT /
   */
  updateProject(request: UpdateProjectRequest): Promise<UpdateProjectResponse>;
  createProject(request: CreateProjectRequest): Promise<CreateProjectResponse>;
  updateProjectStakeholders(
    request: UpdateProjectStakeholdersRequest,
  ): Promise<UpdateProjectStakeholdersResponse>;
  uploadRequirementsSheet(
    request: UploadRequirementsSheetRequest,
  ): Promise<UploadRequirementsSheetResponse>;
  updateProjectMemo(
    request: UpdateProjectMemoRequest,
  ): Promise<UpdateProjectMemoResponse>;

  /**
   * DELETE /
   */
  deleteProjectStakeholders(
    request: DeleteProjectStakeholdersRequest,
  ): Promise<DeleteProjectStakeholdersResponse>;
}

const REST_RESOURCE_NAME = `/project`;

/**
 * GET /project/{pjtId}
 */
const getProject = restResourceRequester<GetProjectRequest, GetProjectResponse>(
  "GET",
  (request) =>
    `${REST_RESOURCE_NAME}/${request.pjtId}?dfnSetVerNum=${request.dfnSetVerNum}`,
);

/**
 * GET /project/{pjtId}/tickets
 */
const getProjectTickets = restResourceRequester<
  GetProjectTicketsRequest,
  GetProjectTicketsResponse
>(
  "GET",
  (request) =>
    `${REST_RESOURCE_NAME}/${request.pjtId}/tickets?dfnSetVerNum=${request.dfnSetVerNum}`,
);

/**
 * GET /project/{pjtId}/requirements-sheet
 */
const downloadRequirementsSheet =
  restResourceDownloader<DownloadRequirementsSheetRequest>(
    "GET",
    (request) => `${REST_RESOURCE_NAME}/${request.pjtId}/requirements-sheet`,
  );

/**
 * PUT /project/{pjtId}
 */
const updateProject = restResourceRequester<
  UpdateProjectRequest,
  UpdateProjectResponse
>(
  "PUT",
  (request) => `${REST_RESOURCE_NAME}/${request.pjtId}`,
  (request) => JSON.stringify(request),
);

/**
 * PUT /project
 */
const createProject = restResourceRequester<
  CreateProjectRequest,
  CreateProjectResponse
>(
  "PUT",
  (_) => `${REST_RESOURCE_NAME}`,
  (request) => JSON.stringify(request),
);

/**
 * PUT /project/{pjtId}/stakeholders
 */
const updateProjectStakeholders = restResourceRequester<
  UpdateProjectStakeholdersRequest,
  UpdateProjectStakeholdersResponse
>(
  "PUT",
  (request) => `${REST_RESOURCE_NAME}/${request.pjtId}/stakeholders`,
  (request) => JSON.stringify(request),
);

/**
 * PUT /project/{pjtId}/requirements-sheet
 */
const uploadRequirementsSheet = restResourceUploader<
  UploadRequirementsSheetRequest,
  UploadRequirementsSheetResponse
>((request) => `${REST_RESOURCE_NAME}/${request.pjtId}/requirements-sheet`);

/**
 * DELETE /project/{pjtId}/stakeholders
 */
const deleteProjectStakeholders = restResourceRequester<
  DeleteProjectStakeholdersRequest,
  DeleteProjectStakeholdersResponse
>(
  "DELETE",
  (request) => `${REST_RESOURCE_NAME}/${request.pjtId}/stakeholders`,
  (request) => JSON.stringify(request),
);

/**
 * PUT /project/{pjtId}/memo
 */
const updateProjectMemo = restResourceRequester<
  UpdateProjectMemoRequest,
  UpdateProjectMemoResponse
>(
  "PUT",
  (request) => `${REST_RESOURCE_NAME}/${request.pjtId}/memo`,
  (request) => JSON.stringify(request),
);

export const projectsRestResource: ProjectsRestResource = {
  getProject: getProject,
  updateProject: updateProject,
  createProject: createProject,
  updateProjectStakeholders: updateProjectStakeholders,
  deleteProjectStakeholders: deleteProjectStakeholders,
  getProjectTickets: getProjectTickets,
  uploadRequirementsSheet: uploadRequirementsSheet,
  downloadRequirementsSheet: downloadRequirementsSheet,
  updateProjectMemo: updateProjectMemo,
};
